import type { FramePoint, HoleMap as HoleMapData, HoleShot } from '../api/public'

// The arithmetic behind a hole map, apart from the component so React Fast
// Refresh keeps working on HoleMap.tsx (a file exporting both components and
// functions loses it).

export type MapView = 'drawn' | 'aerial'

export interface MapPlayer {
  name: string
  /** Index into the app's validated categorical palette (`.series-N`). */
  series: number
  shots: HoleShot[]
}

export const PAD_M = 30
export const MIN_ASPECT = 0.42
const YARD_M = 0.9144

/** Where the hole is played FROM and TO on this map. */
export function holeLine(
  map: HoleMapData | null,
  tee: string | undefined,
  pin: FramePoint | null,
  players: MapPlayer[],
): { from: FramePoint | null; to: FramePoint | null } {
  const from =
    (map && tee && map.tees[tee]) ||
    (map && Object.values(map.tees)[0]) ||
    players.flatMap((p) => p.shots).find((s) => s.shot_number === 1)?.from ||
    null
  const to =
    pin ?? map?.green_center ?? (map && Object.values(map.pins)[0]) ?? lastShotEnd(players) ?? null
  return { from, to }
}

function lastShotEnd(players: MapPlayer[]): FramePoint | undefined {
  const all = players.flatMap((p) => p.shots)
  return all.length ? all[all.length - 1].to : undefined
}

/** The hole's length as GSPro measures it: tee, through the aim points, to the green. */
export function holeYards(map: HoleMapData | null, tee: string | undefined): number | null {
  if (!map) return null
  const from = (tee && map.tees[tee]) || Object.values(map.tees)[0]
  const to = map.green_center ?? Object.values(map.pins)[0]
  if (!from || !to) return null
  const pts = [from, ...map.aim_points, to]
  let m = 0
  for (let i = 1; i < pts.length; i++)
    m += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
  return Math.round(m / YARD_M)
}

/** The view a page should show: what the address asks for, if the hole has it. */
export function chosenView(asked: string | null, hasAerial: boolean): MapView {
  if (asked === 'drawn') return 'drawn'
  if (asked === 'aerial' && hasAerial) return 'aerial'
  return hasAerial ? 'aerial' : 'drawn'
}

const FOOT_M = 0.3048

/**
 * How far a shot went, straight from where it was struck to where it stopped:
 * yards for a full shot, FEET for a putt -- which is how a golfer says each,
 * and a 9-yard putt reads as a misprint. A display figure, never a statistic.
 */
export function shotLength(s: HoleShot): string {
  const m = Math.hypot(s.to[0] - s.from[0], s.to[1] - s.from[1])
  if (s.lie_from === 'green') return `${Math.round(m / FOOT_M)} ft`
  return `${Math.round(m / YARD_M)} yd`
}

/** How far a shot left the ball from the pin, in the same units as shotLength. */
export function leftToPin(s: HoleShot, pin: FramePoint | null): string | null {
  if (!pin) return null
  const m = Math.hypot(pin[0] - s.to[0], pin[1] - s.to[1])
  if (m < 0.3) return 'holed'
  if (s.lie_to === 'green' || m < 20) return `${Math.round(m / FOOT_M)} ft`
  return `${Math.round(m / YARD_M)} yd`
}

/** A lie in words, or a dash where the producer did not say. */
export function lieWords(lie: HoleShot['lie_from']): string {
  return lie ?? '—'
}
